/**
 * Os ícones próprios do HUD V3 (techspec-hud-v3 §03), gerados do `IC` do protótipo
 * (docs/prototipos/fonte/hud-v3-modelo.html): um desenho e uma cor cada, legíveis a 13–30 px.
 * Gerado por scratchpad/impl/gerar-icones.mjs; mexer à mão é ok, o protótipo é a referência.
 */
import { useId } from 'react';
import { SimboloMarca } from '../marca/SimboloMarca';

type P = { className?: string; title?: string };
const cls = (base: string, c?: string) => (c ? `${base} ${c}` : base);
/** Ids de gradiente únicos por cópia (o mesmo ícone pode estar duas vezes na tela). */
const useGid = () => useId().replace(/[^A-Za-z0-9_-]/g, '');

export function IcVida({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M12 21s-7.5-4.6-9.6-9.4C.9 8 3 4 6.9 4c2.2 0 3.7 1.3 5.1 3 1.4-1.7 2.9-3 5.1-3C21 4 23.1 8 21.6 11.6 19.5 16.4 12 21 12 21z" fill="#d2453b" stroke="#5a1410" strokeWidth="1.2"/>
    </svg>
  );
}

export function IcMana({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <circle cx="12" cy="12.5" r="8.8" fill="#3a66d8" stroke="#13265e" strokeWidth="1.2"/><path d="M7.2 14.2a5 5 0 0 0 8.8 2.6" stroke="#9fb8ff" strokeWidth="1.6" fill="none" strokeLinecap="round"/><ellipse cx="9.4" cy="8.8" rx="2.6" ry="1.7" fill="#d6e1ff" opacity=".9"/>
    </svg>
  );
}

export function IcVigor({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M13.6 2 5 13.4h5.6L9.4 22 19 10.2h-5.8z" fill="#f2c94c" stroke="#6b5310" strokeWidth="1.1" strokeLinejoin="round"/>
    </svg>
  );
}

export function IcFadiga({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M6 2.8h12M6 21.2h12" stroke="#1f6e66" strokeWidth="2.2" strokeLinecap="round"/><path d="M7.5 3.6h9c0 4.6-3 6-4.5 8.4 1.5 2.4 4.5 3.8 4.5 8.4h-9c0-4.6 3-6 4.5-8.4C10.5 9.6 7.5 8.2 7.5 3.6z" fill="#3cc6b8" stroke="#17504a" strokeWidth="1.1" strokeLinejoin="round"/><path d="M9.6 19.2h4.8L12 16.4z" fill="#e8fffb"/>
    </svg>
  );
}

export function IcDano({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M20.5 2 22 3.5 11.2 14.3 9.7 12.8z" fill="#d9dee5" stroke="#4a5058" strokeWidth=".9" strokeLinejoin="round"/><path d="M6.6 11.2l6.2 6.2" stroke="#c9a227" strokeWidth="2.4" strokeLinecap="round"/><path d="M9 15 4.3 19.7" stroke="#7a4a26" strokeWidth="2.6" strokeLinecap="round"/><circle cx="3.6" cy="20.4" r="1.7" fill="#c9a227"/>
    </svg>
  );
}

/** O punho fechado: o slot de arma vazio (o personagem luta com as mãos, o soco da UO). */
export function IcPunho({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <g fill="#d8b48c" stroke="#4a2e18" strokeWidth="1" strokeLinejoin="round">
        <rect x="8.6" y="17.5" width="7.2" height="5" rx="1.2" fill="#a77d55"/>
        <path d="M5 10.5c0-1.4 1.1-2.5 2.5-2.5h9c1.4 0 2.5 1.1 2.5 2.5V15c0 2.2-1.8 4-4 4H9c-2.2 0-4-1.8-4-4z"/>
        <rect x="5" y="5.2" width="3.6" height="5.6" rx="1.8"/>
        <rect x="8.6" y="4.4" width="3.6" height="6.2" rx="1.8"/>
        <rect x="12.2" y="4.6" width="3.6" height="6" rx="1.8"/>
        <rect x="15.8" y="5.6" width="3.4" height="5.2" rx="1.7"/>
        <path d="M5.2 12.6c1.8-.3 3.8-.3 6 .4 1 .3 1.3 1.6.4 2.2-1.6 1-3.7 1.2-5.6.8"/>
      </g>
    </svg>
  );
}

export function IcTaticas({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M20.5 2 22 3.5 11.2 14.3 9.7 12.8z" fill="#d9dee5" stroke="#4a5058" strokeWidth=".9" strokeLinejoin="round"/><path d="M6.6 11.2l6.2 6.2" stroke="#c9a227" strokeWidth="2.4" strokeLinecap="round"/><path d="M9 15 4.3 19.7" stroke="#7a4a26" strokeWidth="2.6" strokeLinecap="round"/><circle cx="3.6" cy="20.4" r="1.7" fill="#c9a227"/><g transform="translate(24 0) scale(-1 1)"><path d="M20.5 2 22 3.5 11.2 14.3 9.7 12.8z" fill="#d9dee5" stroke="#4a5058" strokeWidth=".9" strokeLinejoin="round"/><path d="M6.6 11.2l6.2 6.2" stroke="#c9a227" strokeWidth="2.4" strokeLinecap="round"/><path d="M9 15 4.3 19.7" stroke="#7a4a26" strokeWidth="2.6" strokeLinecap="round"/><circle cx="3.6" cy="20.4" r="1.7" fill="#c9a227"/></g>
    </svg>
  );
}

export function IcAr({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M12 2.2 20 5v6.2c0 5-3.4 8.9-8 10.6-4.6-1.7-8-5.6-8-10.6V5z" fill="#9aa6b3" stroke="#3b424b" strokeWidth="1.2" strokeLinejoin="round"/><path d="M12 4.6v14.8" stroke="#d6dde6" strokeWidth="1.4" opacity=".7"/>
    </svg>
  );
}

export function IcRm({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M12 2.2 20 5v6.2c0 5-3.4 8.9-8 10.6-4.6-1.7-8-5.6-8-10.6V5z" fill="#6f5fd8" stroke="#2c2463" strokeWidth="1.2" strokeLinejoin="round"/><path d="M12 6.6l1.4 3.6 3.6 1.4-3.6 1.4L12 16.6l-1.4-3.6-3.6-1.4 3.6-1.4z" fill="#efeaff"/>
    </svg>
  );
}

export function IcVeneno({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M12 2.5C9 7 5.8 10.3 5.8 14.2a6.2 6.2 0 0 0 12.4 0C18.2 10.3 15 7 12 2.5z" fill="#5cbf3c" stroke="#1f4d14" strokeWidth="1.2" strokeLinejoin="round"/><path d="M9.2 13.6a3 3 0 0 0 2 3" stroke="#d6f5c9" strokeWidth="1.4" strokeLinecap="round" fill="none"/>
    </svg>
  );
}

export function IcParalisa({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M12 2.5v19M3.8 7.2l16.4 9.6M3.8 16.8l16.4-9.6M9.4 3.9 12 6.4l2.6-2.5M9.4 20.1 12 17.6l2.6 2.5" stroke="#123a48" strokeWidth="4.4" strokeLinecap="round" fill="none"/><path d="M12 2.5v19M3.8 7.2l16.4 9.6M3.8 16.8l16.4-9.6M9.4 3.9 12 6.4l2.6-2.5M9.4 20.1 12 17.6l2.6 2.5" stroke="#9fe3ff" strokeWidth="2.2" strokeLinecap="round" fill="none"/>
    </svg>
  );
}

export function IcVel({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M3 8h6M2 12h7M3 16h6" stroke="#e6c452" strokeWidth="2" strokeLinecap="round"/><path d="M12 5l7 7-7 7" fill="none" stroke="#e6c452" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export function IcMagia({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M12 2l2.2 7.8L22 12l-7.8 2.2L12 22l-2.2-7.8L2 12l7.8-2.2z" fill="#b48cff" stroke="#3d2470" strokeWidth="1"/>
    </svg>
  );
}

export function IcRelogio({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <circle cx="12" cy="12" r="9" fill="none" stroke="#e6c452" strokeWidth="2"/><path d="M12 7v5.2l3.4 2" stroke="#e6c452" strokeWidth="2" strokeLinecap="round" fill="none"/>
    </svg>
  );
}

export function IcPeso({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M8.5 8.5a3.5 3.5 0 1 1 7 0" fill="none" stroke="#9aa6b3" strokeWidth="2"/><path d="M6.2 9h11.6l2.2 11.5H4z" fill="#6b737e" stroke="#2f343b" strokeWidth="1.1" strokeLinejoin="round"/>
    </svg>
  );
}

export function IcMonstro({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M12 2C7 2 3.5 5.6 3.5 10.2c0 2.7 1.3 4.6 3 5.8V19a1 1 0 0 0 1 1h1.5v-2h2v2h2v-2h2v2h1.5a1 1 0 0 0 1-1v-3c1.7-1.2 3-3.1 3-5.8C20.5 5.6 17 2 12 2zm-3.5 11a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm7 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" fill="#d9dee5" stroke="#3b424b" strokeWidth=".8"/>
    </svg>
  );
}

export function IcJogador({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <circle cx="12" cy="7.2" r="4.2" fill="#7fb0e8" stroke="#1d3a5e" strokeWidth="1.1"/><path d="M3.8 21.2c0-4.6 3.7-8.1 8.2-8.1s8.2 3.5 8.2 8.1z" fill="#7fb0e8" stroke="#1d3a5e" strokeWidth="1.1" strokeLinejoin="round"/>
    </svg>
  );
}

export function IcViajar({ className, title }: P) {
  const g = useGid();
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <defs><radialGradient id={g + 'vjp'} cx=".5" cy=".45" r=".7"><stop offset="0" stopColor="#ffffff"/><stop offset=".25" stopColor="#c8e2ff"/><stop offset=".6" stopColor="#4f86f0"/><stop offset="1" stopColor="#1c2a86"/></radialGradient><linearGradient id={g + 'vjo'} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#fdeaa6"/><stop offset=".5" stopColor="#e2b33c"/><stop offset="1" stopColor="#946510"/></linearGradient></defs><path d="M5.2 21.5V10.2a6.8 6.8 0 0 1 13.6 0v11.3z" fill={`url(#${g}vjp)`}/><path d="M12 6.4c2.9.6 4 3.9 2.2 6.1-1.5 1.8-4.4 1.4-4.8-.6-.3-1.5 1-2.7 2.5-2.2" fill="none" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" opacity=".9"/><path d="M12.2 19.4c-2.6-.6-3.9-3-3.1-5.4" fill="none" stroke="#fff" strokeWidth="1" strokeLinecap="round" opacity=".55"/><path d="M5.2 21.5V10.2a6.8 6.8 0 0 1 13.6 0v11.3" fill="none" stroke="#2a1d06" strokeWidth="3.6" strokeLinejoin="round"/><path d="M5.2 21.5V10.2a6.8 6.8 0 0 1 13.6 0v11.3" fill="none" stroke={`url(#${g}vjo)`} strokeWidth="2.2" strokeLinejoin="round"/><path d="M2.8 21.8h18.4" stroke="#2a1d06" strokeWidth="2.4" strokeLinecap="round"/><path d="M2.8 21.6h18.4" stroke={`url(#${g}vjo)`} strokeWidth="1.5" strokeLinecap="round"/><circle cx="12" cy="3.2" r="1.3" fill={`url(#${g}vjo)`} stroke="#2a1d06" strokeWidth=".7"/>
    </svg>
  );
}

export function IcCidade({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M3.5 21V10l2.5-2 2.5 2v2.5h2V6.5L12 4l1.5 2.5v6h2V10l2.5-2 2.5 2v11z" fill="#c9b27a" stroke="#4a3d1e" strokeWidth="1.1" strokeLinejoin="round"/><path d="M10 21v-4.2a2 2 0 0 1 4 0V21z" fill="#4a3d1e"/>
    </svg>
  );
}

export function IcCasa({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M3 11.5 12 4l9 7.5h-2.5V20h-13v-8.5z" fill="#c9b27a" stroke="#4a3d1e" strokeWidth="1.1" strokeLinejoin="round"/><path d="M10 20v-5h4v5z" fill="#4a3d1e"/><path d="M2.5 11.8 12 3.6l9.5 8.2" fill="none" stroke="#8a3b2a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export function IcPicareta({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M3 8.5C6.5 4 13 2.6 18.5 4.4 13.8 4.6 9 6 5.6 9.4z" fill="#b9c2cc" stroke="#3b424b" strokeWidth="1" strokeLinejoin="round"/><path d="M10.2 6.6 20.6 21" stroke="#7a4a26" strokeWidth="2.6" strokeLinecap="round"/>
    </svg>
  );
}

export function IcLupa({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <circle cx="10.2" cy="10.2" r="6.4" fill="none" stroke="#c9cdd6" strokeWidth="2.2"/><path d="M15 15l5.6 5.6" stroke="#c9cdd6" strokeWidth="2.6" strokeLinecap="round"/>
    </svg>
  );
}

export function IcEtiqueta({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M3 12.2V4.2a1.2 1.2 0 0 1 1.2-1.2h8l8.6 8.6a1.4 1.4 0 0 1 0 2l-7 7a1.4 1.4 0 0 1-2 0z" fill="#e6c452" stroke="#6b5310" strokeWidth="1.1" strokeLinejoin="round"/><circle cx="7.6" cy="7.6" r="1.7" fill="#6b5310"/>
    </svg>
  );
}

export function IcPergaminho({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M6 4h11.5a2.5 2.5 0 0 1 0 5H17v9.5a2.5 2.5 0 0 1-2.5 2.5H5a2.5 2.5 0 0 1 0-5h1z" fill="#e9dcae" stroke="#6b5a2a" strokeWidth="1.1" strokeLinejoin="round"/><path d="M9 9h5M9 12h5M9 15h3" stroke="#6b5a2a" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  );
}

export function IcBalao({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8a2.5 2.5 0 0 1-2.5 2.5H11l-4.5 4v-4h0A2.5 2.5 0 0 1 4 13.5z" fill="#e9dcae" stroke="#6b5a2a" strokeWidth="1.1" strokeLinejoin="round"/><path d="M8 8.5h8M8 11.5h5" stroke="#6b5a2a" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  );
}

export function IcGlobo({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <circle cx="12" cy="12" r="8.8" fill="#2b5fae" stroke="#0f2a55" strokeWidth="1.1"/><path d="M3.4 12h17.2M12 3.2c-3 3.2-3 14.4 0 17.6M12 3.2c3 3.2 3 14.4 0 17.6M5 7.5h14M5 16.5h14" fill="none" stroke="#a9c6ff" strokeWidth="1.1"/>
    </svg>
  );
}

export function IcCofre({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <rect x="2.5" y="8" width="19" height="12.5" rx="1.6" fill="#8a5a2b" stroke="#3d240d" strokeWidth="1.1"/><path d="M2.5 11.5h19" stroke="#3d240d" strokeWidth="1.1"/><path d="M4 8c0-3 2.5-4.5 8-4.5S20 5 20 8" fill="#a06a33" stroke="#3d240d" strokeWidth="1.1"/><rect x="10" y="10" width="4" height="4.6" rx=".8" fill="#e6c452" stroke="#6b5310" strokeWidth=".8"/>
    </svg>
  );
}

export function IcMaisGente({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <circle cx="9.5" cy="7.5" r="3.8" fill="#7fb0e8" stroke="#1d3a5e" strokeWidth="1.1"/><path d="M2.5 20.5c0-4.2 3.1-7.2 7-7.2s7 3 7 7.2z" fill="#7fb0e8" stroke="#1d3a5e" strokeWidth="1.1" strokeLinejoin="round"/><path d="M18.5 7v7M15 10.5h7" stroke="#7be07b" strokeWidth="2.4" strokeLinecap="round"/>
    </svg>
  );
}

export function IcPena({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M20.5 3.5C12 4 6.5 9.5 5 18l2 .5C9 12 13 8 20.5 3.5z" fill="#e9dcae" stroke="#6b5a2a" strokeWidth="1.1" strokeLinejoin="round"/><path d="M5 18 3.5 21" stroke="#6b5a2a" strokeWidth="1.6" strokeLinecap="round"/><path d="M9 14l3-1.2M11.5 10.5l3.4-1" stroke="#6b5a2a" strokeWidth="1"/>
    </svg>
  );
}

export function IcBencao({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M12 2.2 20 5v6.2c0 5-3.4 8.9-8 10.6-4.6-1.7-8-5.6-8-10.6V5z" fill="#e6c452" stroke="#6b5310" strokeWidth="1.2" strokeLinejoin="round"/><path d="M12 7.6v9M9 10.6h6" stroke="#5a3d06" strokeWidth="2" strokeLinecap="round"/><circle cx="12" cy="6.3" r="1.5" fill="none" stroke="#5a3d06" strokeWidth="1.4"/>
    </svg>
  );
}

export function IcCoroa({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="-7 -6 14 11" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M-6 4V-4l3 3.5L0-5l3 4.5L6-4v8z" fill="#e6c452" stroke="rgba(0,0,0,.85)" strokeWidth="1" strokeLinejoin="round"/><rect x="-6" y="1.6" width="12" height="2.4" fill="rgba(0,0,0,.28)"/>
    </svg>
  );
}

export function IcCadeado({ className, title }: P) {
  return (
    <svg className={cls('ch-ic', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M8 10.5V7.6a4 4 0 0 1 8 0v2.9" fill="none" stroke="#9aa6b3" strokeWidth="2.2"/><rect x="5" y="10.5" width="14" height="10.5" rx="2" fill="#9aa6b3" stroke="#3b424b" strokeWidth="1.1"/>
    </svg>
  );
}

/** A marca do Ultima Idle (a rosa dos ventos, components/marca): o ícone de Caçar. */
export function MarcaUltimaIdle({ className }: { className?: string }) {
  return <SimboloMarca className={cls('v3-marca', className)} />;
}

export const ICONES_V3 = {
  vida: IcVida,
  mana: IcMana,
  vigor: IcVigor,
  fadiga: IcFadiga,
  dano: IcDano,
  taticas: IcTaticas,
  ar: IcAr,
  rm: IcRm,
  veneno: IcVeneno,
  paralisa: IcParalisa,
  vel: IcVel,
  magia: IcMagia,
  relogio: IcRelogio,
  peso: IcPeso,
  monstro: IcMonstro,
  jogador: IcJogador,
  viajar: IcViajar,
  cidade: IcCidade,
  picareta: IcPicareta,
  lupa: IcLupa,
  etiqueta: IcEtiqueta,
  pergaminho: IcPergaminho,
  balao: IcBalao,
  globo: IcGlobo,
  cofre: IcCofre,
  maisGente: IcMaisGente,
  pena: IcPena,
  bencao: IcBencao,
  coroa: IcCoroa,
  cadeado: IcCadeado
} as const;
export type IconeV3 = keyof typeof ICONES_V3;

export function IconeV3({ nome, className, title }: P & { nome: IconeV3 }) {
  const C = ICONES_V3[nome];
  return <C className={className} title={title} />;
}

/** Guarda-roupa (techspec-guardaroupa): o cabide. */
export function IcGuardaRoupa({ className, title }: P) {
  return (
    <svg className={cls('ch-ic ic-gr', className)} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d="M12 6a2 2 0 1 1 2 2c-1 0-2 .7-2 1.6V11" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 11 3 17.5c-.7.5-.4 1.5.5 1.5h17c.9 0 1.2-1 .5-1.5z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
